import React, { useState, useEffect } from 'react';
import expensesApi from '../api/expenses';
import { Expense, ExpenseStatus, TransactionType, EXPENSE_STATUS_LABELS, EXPENSE_STATUS_COLORS } from '../types/expense';
import Pagination from './Pagination';
import ExpenseEditModal from './ExpenseEditModal';
import ConfirmDialog from './ConfirmDialog';
import InfoTooltip from './InfoTooltip';
import { useAuth } from '../contexts/AuthContext';

const ALL_INLINE_STATUSES: ExpenseStatus[] = ['paid', 'unpaid', 'no_payment_required', 'pay_today'];

interface ExpenseListProps {
  refreshTrigger?: number;
  type?: TransactionType;
}

const LIMIT_OPTIONS = [10, 25, 50, 100];

interface ConfirmDialogState {
  isOpen: boolean;
  expenseId: string | null;
  expenseName: string | null;
}

const ExpenseList: React.FC<ExpenseListProps> = ({ refreshTrigger, type }) => {
  const { user, isOperator, isTreasurer, isAdmin } = useAuth();

  // Operator: no status control at all
  // Treasurer: sees pay_today (disabled, view-only) but cannot set it
  // Admin: all statuses
  const inlineStatuses: ExpenseStatus[] = isAdmin
    ? ALL_INLINE_STATUSES
    : isTreasurer
      ? ALL_INLINE_STATUSES
      : []; // operator gets empty — status dropdown hidden entirely
  const isInlineStatusDisabled = (s: ExpenseStatus): boolean =>
    isTreasurer && !isAdmin && s === 'pay_today';

  const canChangeStatus = !isOperator;

  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [pulsingIds, setPulsingIds] = useState<Set<string>>(new Set());
  const [needsDateIds, setNeedsDateIds] = useState<Set<string>>(new Set());
  const [toastVariant, setToastVariant] = useState<'success' | 'error'>('success');
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    isOpen: false,
    expenseId: null,
    expenseName: null,
  });

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const showToast = (message: string, variant: 'success' | 'error' = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setTimeout(() => setToastMessage(null), 4000);
  };

  const MIN_PULSE_MS = 3000;
  const COLLAPSE_MS = 300;

  const beginPulse = (expenseId: string): number => {
    setPulsingIds((prev) => {
      if (prev.has(expenseId)) return prev;
      const next = new Set(prev);
      next.add(expenseId);
      return next;
    });
    return Date.now();
  };

  const stopPulse = (expenseId: string) => {
    setPulsingIds((prev) => {
      if (!prev.has(expenseId)) return prev;
      const next = new Set(prev);
      next.delete(expenseId);
      return next;
    });
  };

  const collapseAndRemove = (expenseId: string) => {
    setRemovingIds((prev) => new Set(prev).add(expenseId));
    setTimeout(() => {
      setExpenses((prev) => prev.filter((e) => e.id !== expenseId));
      setTotal((prev) => prev - 1);
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.delete(expenseId);
        return next;
      });
      showToast(type === 'income' ? 'Доход перенесён в Журнал операций' : 'Расход перенесён в Журнал операций');
    }, COLLAPSE_MS);
  };

  // Wait until both: (a) the request has resolved (caller awaits before invoking us),
  // (b) at least MIN_PULSE_MS elapsed since pulse started. Then stop pulse + collapse.
  const finishPulseAndRemove = async (expenseId: string, startedAt: number) => {
    const elapsed = Date.now() - startedAt;
    const remaining = Math.max(0, MIN_PULSE_MS - elapsed);
    if (remaining > 0) {
      await new Promise((resolve) => setTimeout(resolve, remaining));
    }
    stopPulse(expenseId);
    collapseAndRemove(expenseId);
  };

  // Standalone helper for cases where there's no pending request to await
  // (e.g. confirmed income auto-fade, edit-modal callback). Pulses for MIN_PULSE_MS, then collapses.
  const animateRemoval = (expenseId: string) => {
    const startedAt = beginPulse(expenseId);
    void finishPulseAndRemove(expenseId, startedAt);
  };

  // Reset to page 1 when type changes
  useEffect(() => {
    setPage(1);
  }, [type]);

  // Single effect for fetching — depends directly on all query params
  useEffect(() => {
    const abortController = new AbortController();

    setIsLoading(true);
    expensesApi.getAll(page, limit, type, abortController.signal)
      .then((response) => {
        setExpenses(response.data);
        setTotalPages(response.totalPages);
        setTotal(response.total);
      })
      .catch((error: any) => {
        if (abortController.signal.aborted) return;
        console.error('Error fetching expenses:', error);
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      });

    return () => abortController.abort();
  }, [page, limit, type, refreshTrigger]);

  const openDeleteDialog = (expense: Expense) => {
    setConfirmDialog({
      isOpen: true,
      expenseId: expense.id,
      expenseName: expense.name,
    });
  };

  const closeDialog = () => {
    setConfirmDialog({
      isOpen: false,
      expenseId: null,
      expenseName: null,
    });
  };

  const handleDialogConfirm = async () => {
    if (!confirmDialog.expenseId) return;

    const id = confirmDialog.expenseId;
    closeDialog();

    try {
      await expensesApi.delete(id);
      setExpenses(expenses.filter((e) => e.id !== id));
      setTotal(total - 1);
      showToast('Запись перемещена в корзину');
    } catch (error: any) {
      showToast(error.response?.data?.message || 'Ошибка при удалении', 'error');
    }
  };

  const handleExpenseUpdate = (updatedExpense: Expense) => {
    if (updatedExpense.isConfirmed && type !== 'income') {
      // Exported to Google Sheets — animate removal (expenses only)
      setExpenses(expenses.map((e) => (e.id === updatedExpense.id ? updatedExpense : e)));
      animateRemoval(updatedExpense.id);
    } else {
      setExpenses(expenses.map((e) => (e.id === updatedExpense.id ? updatedExpense : e)));
    }
  };

  const handlePayTodayToggle = (expense: Expense) => {
    const newStatus: ExpenseStatus = expense.status === 'pay_today' ? 'unpaid' : 'pay_today';
    handleInlineStatusChange(expense, newStatus);
  };

  const handleInlineStatusChange = async (expense: Expense, newStatus: ExpenseStatus) => {
    if (newStatus === expense.status) return;

    // When switching to paid, show date field and wait for user to pick a date
    if (newStatus === 'paid') {
      const optimistic = { ...expense, status: newStatus, paymentDate: null };
      setExpenses((prev) => prev.map((e) => (e.id === expense.id ? optimistic : e)));
      setNeedsDateIds((prev) => new Set(prev).add(expense.id));
      return;
    }

    // Clear needsDate if switching away from paid
    setNeedsDateIds((prev) => {
      if (!prev.has(expense.id)) return prev;
      const next = new Set(prev);
      next.delete(expense.id);
      return next;
    });

    const paymentDate = newStatus === 'no_payment_required' ? null : undefined;

    // Optimistic update
    const optimistic = { ...expense, status: newStatus, ...(paymentDate !== undefined ? { paymentDate } : {}) };
    setExpenses((prev) => prev.map((e) => (e.id === expense.id ? optimistic : e)));

    // Pulse starts BEFORE the request when the new status will move the card to the journal
    // (no_payment_required, or paid+date). Continues for MIN_PULSE_MS minimum AND until request resolves.
    const willExport = newStatus === 'no_payment_required';
    const startedAt = willExport ? beginPulse(expense.id) : 0;

    try {
      const dataToSend: any = { status: newStatus };
      if (paymentDate !== undefined) dataToSend.paymentDate = paymentDate;

      const updated = await expensesApi.update(expense.id, dataToSend);
      if (updated.isConfirmed && type !== 'income') {
        setExpenses((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
        if (willExport) {
          await finishPulseAndRemove(updated.id, startedAt);
        } else {
          // Server confirmed export but we didn't predict it — fall back to standalone pulse.
          animateRemoval(updated.id);
        }
      } else {
        if (willExport) stopPulse(expense.id);
        setExpenses((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
      }
    } catch (error: any) {
      if (willExport) stopPulse(expense.id);
      setExpenses((prev) => prev.map((e) => (e.id === expense.id ? expense : e)));
      showToast(error.response?.data?.message || 'Ошибка при обновлении статуса', 'error');
    }
  };

  const handleInlinePaymentDateChange = async (expense: Expense, newDate: string) => {
    const paymentDate = newDate || null;

    // Clear needsDate validation when date is provided
    if (paymentDate && needsDateIds.has(expense.id)) {
      setNeedsDateIds((prev) => {
        const next = new Set(prev);
        next.delete(expense.id);
        return next;
      });
    }

    // Optimistic update
    const optimistic = { ...expense, paymentDate };
    setExpenses((prev) => prev.map((e) => (e.id === expense.id ? optimistic : e)));

    // Setting a paymentDate while status is "paid" triggers journal export — pulse from request start.
    const willExport = expense.status === 'paid' && !!paymentDate;
    const startedAt = willExport ? beginPulse(expense.id) : 0;

    try {
      const updated = await expensesApi.update(expense.id, { status: expense.status, paymentDate });
      if (updated.isConfirmed && type !== 'income') {
        setExpenses((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
        if (willExport) {
          await finishPulseAndRemove(updated.id, startedAt);
        } else {
          animateRemoval(updated.id);
        }
      } else {
        if (willExport) stopPulse(expense.id);
        setExpenses((prev) => prev.map((e) => (e.id === updated.id ? updated : e)));
      }
    } catch (error: any) {
      if (willExport) stopPulse(expense.id);
      setExpenses((prev) => prev.map((e) => (e.id === expense.id ? expense : e)));
      showToast(error.response?.data?.message || 'Ошибка при обновлении даты оплаты', 'error');
    }
  };

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getCurrencySymbol = (currency: string): string => {
    const symbols: { [key: string]: string } = {
      BYN: 'Br',
      EUR: '€',
      USD: '$',
      PLN: 'zł',
      RUB: '₽',
      UAH: '₴',
      GBP: '£',
    };
    return symbols[currency] || currency;
  };

  // Aggregate everything in BYN. For BYN records fall back to `amount` when
  // `amountByn` is missing (e.g. NBRB was unreachable at creation time).
  const toByn = (e: Expense): number =>
    Number(e.amountByn ?? (e.currency === 'BYN' ? e.amount : 0));

  const totalByn = expenses.reduce((sum, e) => sum + toByn(e), 0);

  const payTodayByn = expenses
    .filter((e) => e.status === 'pay_today')
    .reduce((sum, e) => sum + toByn(e), 0);

  const listTitle = type === 'income' ? 'Доходы' : 'Расходы';

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">{listTitle}</h2>
        <p className="text-gray-500 text-center">Загрузка...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-3 sm:p-6">
      <div className="flex justify-between items-center mb-1">
        <h2 className="text-lg sm:text-xl font-semibold">{listTitle}</h2>
        <span className="text-sm text-gray-500">Всего: {total}</span>
      </div>
      {canChangeStatus && type !== 'income' && (
        <p className="text-xs text-gray-400 mb-4">
          При смене статуса на «Не требует оплаты» или «Оплачено» с указанием даты запись переносится в Журнал операций
        </p>
      )}
      {type === 'income' && (
        <p className="text-xs text-gray-400 mb-4">
          Доходы автоматически переносятся в Журнал операций при создании
        </p>
      )}
      {!canChangeStatus && type !== 'income' && <div className="mb-4" />}

      {expenses.length > 0 && (
        <div className="mb-4 p-3 bg-blue-50 rounded-md sticky top-0 z-10 shadow-sm lg:static lg:shadow-none">
          <div className="flex flex-col md:flex-row md:items-start md:gap-6">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-blue-800 mb-1">Итого на странице:</p>
              <span className="text-lg font-semibold text-blue-900">
                {totalByn.toFixed(2)} Br
              </span>
            </div>
            {isAdmin && type !== 'income' && (
              <div className="flex-1 min-w-0 mt-2 md:mt-0">
                <p className="text-sm font-medium text-yellow-800 mb-1">К оплате сегодня:</p>
                <span className="text-lg font-semibold text-yellow-900">
                  {payTodayByn.toFixed(2)} Br
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {toastMessage && (
        <div
          role="status"
          aria-live="polite"
          className={`fixed bottom-4 right-4 z-50 max-w-sm p-3 pr-2 text-sm rounded-md shadow-lg flex items-center gap-3 animate-fade-in ${
            toastVariant === 'error'
              ? 'bg-red-100 text-red-800 border border-red-200'
              : 'bg-green-100 text-green-800 border border-green-200'
          }`}
        >
          <span className="flex-1">{toastMessage}</span>
          <button
            onClick={() => setToastMessage(null)}
            className="p-0.5 hover:opacity-70 shrink-0"
            aria-label="Закрыть уведомление"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {expenses.length === 0 ? (
        <p className="text-gray-500 text-center">{type === 'income' ? 'Нет записей о доходах' : 'Нет записей о расходах'}</p>
      ) : (
        <div className="space-y-3">
          {expenses.map((expense) => (
            <div
              key={expense.id}
              className={`p-3 sm:p-4 rounded-lg border ${
                pulsingIds.has(expense.id)
                  ? expense.type === 'income'
                    ? 'bg-green-100 border-green-400'
                    : 'bg-red-100 border-red-400'
                  : expense.type === 'income' && expense.isConfirmed
                    ? 'bg-green-50 border-green-300'
                    : expense.type !== 'income' && expense.status === 'pay_today'
                      ? 'bg-yellow-50 border-yellow-300'
                      : 'bg-gray-50 border-gray-200'
              } hover:shadow-md transition-all duration-300 ease-out ${removingIds.has(expense.id) ? 'opacity-0 scale-95 max-h-0 !p-0 !m-0 overflow-hidden border-transparent' : 'opacity-100 scale-100'} ${pulsingIds.has(expense.id) ? 'animate-pulse' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm text-gray-500">{formatDate(expense.date)}</span>
                    <span className="font-medium text-gray-800">{expense.name}</span>
                  </div>

                  <div className="flex items-center gap-3 mt-2 flex-wrap">
                    <span className={`text-lg font-semibold ${expense.type === 'income' ? 'text-green-700' : 'text-red-700'}`}>
                      {expense.type === 'income' ? '+' : '−'}{toByn(expense).toFixed(2)} Br
                    </span>
                    {expense.currency !== 'BYN' && (
                      <span className="text-sm text-gray-500">
                        ({Number(expense.amount).toFixed(2)} {getCurrencySymbol(expense.currency)})
                      </span>
                    )}

                    {/* Inline status dropdown — hidden for operator and income */}
                    {type !== 'income' && (
                      canChangeStatus ? (
                        <>
                          <select
                            value={expense.status}
                            onChange={(e) => handleInlineStatusChange(expense, e.target.value as ExpenseStatus)}
                            className="px-2 py-0.5 text-xs rounded-full border border-gray-300 bg-white text-gray-800 cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-300"
                          >
                            {inlineStatuses.map((s) => (
                              <option key={s} value={s} disabled={isInlineStatusDisabled(s)}>
                                {EXPENSE_STATUS_LABELS[s]}
                              </option>
                            ))}
                          </select>
                          <InfoTooltip text="При выборе статусов 'Не требует оплаты' и 'Оплачено' с выбором даты расход будет попадать в Журнал операций в вашей Google папке" />
                        </>
                      ) : (
                        <span className={`px-2 py-0.5 text-xs rounded-full ${EXPENSE_STATUS_COLORS[expense.status]}`}>
                          {EXPENSE_STATUS_LABELS[expense.status]}
                        </span>
                      )
                    )}

                    {/* Pay today toggle — admin only, expenses only */}
                    {type !== 'income' && isAdmin && (expense.status === 'pay_today' || expense.status === 'unpaid') && (
                      <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-gray-300 text-yellow-500 focus:ring-yellow-400 cursor-pointer"
                          checked={expense.status === 'pay_today'}
                          onChange={() => handlePayTodayToggle(expense)}
                        />
                        <span className="text-xs text-yellow-800">К оплате сегодня</span>
                      </label>
                    )}

                    {type !== 'income' && expense.costCategory && (
                      <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full">
                        {expense.costCategory}
                      </span>
                    )}

                    {type === 'income' && expense.isConfirmed && (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded-full">
                        Добавлено в Журнал операций
                      </span>
                    )}
                  </div>

                  {/* Inline payment date when status is paid — hidden for operator and income */}
                  {type !== 'income' && expense.status === 'paid' && canChangeStatus && (
                    <div className="mt-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">
                          Дата оплаты{needsDateIds.has(expense.id) && <span className="text-red-500 ml-0.5">*</span>}
                        </span>
                        <input
                          type="date"
                          value={expense.paymentDate || ''}
                          onChange={(e) => handleInlinePaymentDateChange(expense, e.target.value)}
                          className={`text-sm border rounded px-2 py-0.5 focus:ring-2 ${needsDateIds.has(expense.id) ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : 'border-gray-300 focus:ring-blue-300 focus:border-blue-300'}`}
                        />
                      </div>
                      {needsDateIds.has(expense.id) && (
                        <p className="text-xs text-red-500 mt-1">Укажите дату оплаты</p>
                      )}
                    </div>
                  )}

                  <div className="mt-2 text-sm text-gray-600 space-y-1">
                    {type !== 'income' && expense.status !== 'paid' && expense.paymentDate && (
                      <div>Дата оплаты: {formatDate(expense.paymentDate)}</div>
                    )}
                    {expense.contractor && (
                      <div>Контрагент: {expense.contractor}</div>
                    )}
                    {expense.comment && (
                      <div className="text-gray-500 italic">{expense.comment}</div>
                    )}
                  </div>

                  <div className="mt-1 text-xs text-gray-400 italic break-words" title={expense.originalText}>
                    {expense.originalText}
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  {/* Edit: all roles can edit records (operator cannot change status — enforced in modal/backend) */}
                  <button
                    onClick={() => setEditingExpense(expense)}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded-md transition-colors"
                    title="Редактировать"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>

                  {/* Delete: all roles */}
                  <button
                    onClick={() => openDeleteDialog(expense)}
                    className="p-2 text-red-600 hover:bg-red-100 rounded-md transition-colors"
                    title="Удалить"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Pagination
        currentPage={page}
        totalPages={totalPages}
        onPageChange={setPage}
        limit={limit}
        limitOptions={LIMIT_OPTIONS}
        onLimitChange={handleLimitChange}
        total={total}
      />

      {editingExpense && (
        <ExpenseEditModal
          expense={editingExpense}
          isOpen={!!editingExpense}
          onClose={() => setEditingExpense(null)}
          onSave={handleExpenseUpdate}
        />
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Удалить запись"
        message={`Переместить "${confirmDialog.expenseName}" в корзину?`}
        confirmText="Удалить"
        cancelText="Отмена"
        variant="danger"
        onConfirm={handleDialogConfirm}
        onCancel={closeDialog}
      />
    </div>
  );
};

export default ExpenseList;
